import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPhone,
  FiMail,
  FiUser,
  FiCalendar,
  FiClock,
  FiTag,
  FiEdit,
  FiPlus,
  FiInfo,
  FiX,
  FiSave,
  FiTrash2,
  FiChevronDown,
  FiCheck,
  FiFlag,
  FiZap
} from 'react-icons/fi';
import '../styles/CardSidebar.css';
import SessionService from '../services/SessionService';
import { getTags, Tag } from '../services/LabelService';
import { getUsersSummaryForPicker, User } from '../services/UserService';
import Toast from './Toast';
import { LoadingSpinner } from './LoadingSpinner';
import { getDisplayPhone, formatPhoneNumber, isLidOrJid, PHONE_UNAVAILABLE } from '../utils/formatters';
import { listCrmAutomationLogs, type CrmAutomationLogEntry } from '../services/CrmAutomationService';
import {
  formatConfidence,
  moveAuthorLabel,
  moveOutcomeLabel,
  moveOutcomeTone,
  moveRouteLabel,
} from '../utils/crmMoveLabels';
import { CommercialFieldList } from '../mendsAi/components/CommercialFieldList';
import { getMemory, MemoryFieldDto } from '../mendsAi/services/MendsVisionService';
import OpportunityPanel, { OpportunityPanelColumn } from './crm/OpportunityPanel';
import { logsForCard } from './crm/dossier/dossierFormat';

interface Card {
  id: string;
  title: string;
  content: string;
  email?: string;
  phone?: string;
  assignedTo?: string | number;
  assigneeAvatar?: string;
  createdAt?: string;
  lastContact?: string;
  notes?: string[];
  priority?: 'high' | 'medium' | 'low';
  contactId: number;
  tagId?: number;
  columnId?: number;
  sectorId: number;
  position?: number;
  user_id?: number;
  number?: string;
  name?: string;
  /** Quantos cards (oportunidades) o contato tem no setor. */
  opportunityCount?: number;
}

interface CardSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedCard: Card) => Promise<void>;
  selectedCard: Card | null;
  isNewCard?: boolean;
  columnId?: string;
  addToast?: (message: string, type: 'success' | 'error' | 'info') => void;
  mode?: 'chat' | 'crm';
  /** Colunas do Kanban (para abrir oportunidade nova numa etapa). */
  columns?: OpportunityPanelColumn[];
  /** Oportunidade criada/corrigida: o board recarrega. */
  onOpportunitiesChanged?: () => void;
}

const CardSidebar: React.FC<CardSidebarProps> = ({
  selectedCard,
  isOpen,
  onClose,
  onSave,
  isNewCard = false,
  columnId,
  addToast,
  mode = 'crm',
  columns,
  onOpportunitiesChanged,
}) => {
  const [editingCard, setEditingCard] = useState<Card | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPriorityDropdownOpen, setPriorityDropdownOpen] = useState(false);
  const [isTagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [isResponsibleDropdownOpen, setResponsibleDropdownOpen] = useState(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [crmAutomationLogs, setCrmAutomationLogs] = useState<CrmAutomationLogEntry[]>([]);
  const [crmAutomationLogsLoading, setCrmAutomationLogsLoading] = useState(false);
  const navigate = useNavigate();
  const cardLogs = editingCard
    ? logsForCard(crmAutomationLogs, Number(editingCard.id), editingCard.opportunityCount ?? 1)
    : crmAutomationLogs;
  const [commercialFields, setCommercialFields] = useState<MemoryFieldDto[]>([]);
  const [commercialHistory, setCommercialHistory] = useState<MemoryFieldDto[]>([]);

  useEffect(() => {
    if (!isOpen) setIsSaving(false);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || mode !== 'crm' || !selectedCard?.contactId) {
      setCrmAutomationLogs([]);
      return;
    }
    const sectorId = SessionService.getSectorId();
    if (!sectorId) return;
    let cancelled = false;
    setCrmAutomationLogsLoading(true);
    listCrmAutomationLogs(sectorId, selectedCard.contactId)
      .then(rows => {
        if (!cancelled) setCrmAutomationLogs(rows);
      })
      .catch(() => {
        if (!cancelled) setCrmAutomationLogs([]);
      })
      .finally(() => {
        if (!cancelled) setCrmAutomationLogsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, mode, selectedCard?.contactId]);

  useEffect(() => {
    if (!isOpen || !selectedCard?.contactId) {
      setCommercialFields([]);
      setCommercialHistory([]);
      return;
    }
    const sectorId = selectedCard.sectorId || SessionService.getSectorId();
    if (!sectorId) return;
    const controller = new AbortController();
    getMemory(sectorId, selectedCard.contactId, controller.signal)
      .then((memory) => {
        if (controller.signal.aborted) return;
        setCommercialFields(memory.properties ?? []);
        setCommercialHistory(memory.history ?? []);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setCommercialFields([]);
          setCommercialHistory([]);
        }
      });
    return () => {
      controller.abort();
    };
  }, [isOpen, selectedCard?.contactId, selectedCard?.sectorId]);

  // Initialize editing state when selected card changes
  useEffect(() => {
    if (isNewCard) {
      setEditingCard({
        id: `card${Date.now()}`,
        title: '',
        content: '',
        createdAt: new Date().toISOString().split('T')[0],
        columnId: columnId ? parseInt(columnId) : undefined,
        sectorId: SessionService.getSectorId() || 0,
        position: 0,
        contactId: 0
      });
      setIsEditing(true);
    } else if (selectedCard) {
      const normalizedCard: Card = mode === 'chat' ? {
        ...selectedCard,
        title: selectedCard.name || selectedCard.title,
        phone: selectedCard.number || selectedCard.phone,
        content: selectedCard.notes?.toString() || selectedCard.content
      } : selectedCard;

      setEditingCard(normalizedCard);
    }
  }, [selectedCard, isNewCard, columnId, mode]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.contact-select')) {
        setPriorityDropdownOpen(false);
        setTagDropdownOpen(false);
        setResponsibleDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Load tags when sidebar opens
  useEffect(() => {
    const loadTags = async () => {
      try {
        const sectorId = SessionService.getSectorId() || 0;
        const tagsData = await getTags(sectorId);
        setTags(tagsData.data);
      } catch (error) {
        console.error('Erro ao carregar tags:', error);
      }
    };
    loadTags();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const sid = SessionService.getSectorId();
        if (sid == null || sid <= 0) {
          setUsers([]);
          return;
        }
        const usersData = await getUsersSummaryForPicker(sid);
        setUsers(usersData);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };
    loadUsers();
  }, []);

  if ((!selectedCard && !isNewCard) || !isOpen) return null;

  // Update field on editing card
  const updateEditingCard = (field: keyof Card, value: any) => {
    if (editingCard) {
      setEditingCard({
        ...editingCard,
        [field]: value
      });
    }
  };

  // Enter edit mode
  const startEditing = () => {
    setIsEditing(true);
  };

  // Cancel edit
  const cancelEditing = () => {
    if (isNewCard) {
      onClose();
    } else if (selectedCard) {
      setEditingCard({ ...selectedCard });
      setIsEditing(false);
    }
  };

  // Save changes
  const saveChanges = async () => {
    if (!editingCard || isSaving) return;

    const requiredField = mode === 'chat' ? 'name' : 'title';
    const fieldLabel = mode === 'chat' ? 'contact name' : 'lead title';

    if (!editingCard[requiredField]?.trim() && !editingCard.title?.trim()) {
      addToast?.(`Please fill in the ${fieldLabel}.`, 'error');
      return;
    }

    setPriorityDropdownOpen(false);
    setTagDropdownOpen(false);
    setResponsibleDropdownOpen(false);

    try {
      setIsSaving(true);
      const normalizedCard: Card = mode === 'chat' ? {
        ...editingCard,
        name: editingCard.title,
        number: editingCard.phone,
        notes: editingCard.content ? [editingCard.content] : undefined
      } : editingCard;

      await onSave(normalizedCard);
      addToast?.(mode === 'chat' ? 'Contato atualizado.' : 'Lead atualizado.', 'success');
      handleClose();
    } catch (error) {
      const fallback = mode === 'chat' ? 'Erro ao salvar o contato.' : 'Erro ao salvar o lead.';
      const msg = error instanceof Error && error.message ? error.message : fallback;
      addToast?.(msg, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  // Confirm discard when closing while editing
  const handleClose = () => {
    if (isEditing) {
      setIsEditing(false);
    }
    onClose();
  };

  // Priority dropdown options
  const priorityOptions = [
    { value: 'high', label: 'Alta' },
    { value: 'medium', label: 'Média' },
    { value: 'low', label: 'Baixa' }
  ];

  // react-select styles (match contact picker)
  const selectStyles = {
    control: (base: any) => ({
      ...base,
      backgroundColor: 'var(--bg-base)',
      borderColor: 'var(--border-color)',
      minHeight: '45px',
      '&:hover': {
        borderColor: 'var(--border-color)'
      }
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: 'var(--bg-base)',
      border: '1px solid var(--border-color)'
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused ? 'var(--bg-tertiary)' : 'var(--bg-elevated)',
      color: 'var(--text-primary)',
      '&:hover': {
        backgroundColor: 'var(--bg-base)'
      }
    }),
    singleValue: (base: any) => ({
      ...base,
      color: 'var(--text-primary)'
    }),
    input: (base: any) => ({
      ...base,
      color: 'var(--text-primary)'
    }),
    placeholder: (base: any) => ({
      ...base,
      color: 'var(--text-tertiary)'
    })
  };

  // Format date/time for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Initials from name
  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  return (
    <>
      <div className={`card-sidebar-overlay ${isOpen ? 'open' : ''}`} onClick={handleClose}></div>
      <div className={`card-sidebar ${isOpen ? 'open' : ''}`}>
        <div className="card-sidebar-header">
          <div className="card-sidebar-actions">
            <button className="sidebar-icon-button" onClick={handleClose}>
              <FiX />
            </button>
          </div>
          <h2>
            {isNewCard ? (mode === 'chat' ? "Novo contato" : "Novo lead") :
              isEditing ? (mode === 'chat' ? "Editar contato" : "Editar lead") :
                editingCard?.title}
          </h2>
        </div>

        <div className={`card-sidebar-content ${isSaving ? 'card-sidebar-content--saving' : ''}`}>
          {isEditing || isNewCard ? (
            <div className="card-edit-form" aria-busy={isSaving}>
              <div className="form-group">
                <label htmlFor="edit-title">
                  {mode === 'chat' ? 'Nome do contato' : 'Título do lead'}
                  <span className="required">*</span>
                </label>
                <input
                  id="edit-title"
                  type="text"
                  value={editingCard?.title || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 50) {
                      updateEditingCard('title', value);
                      if (mode === 'chat') {
                        updateEditingCard('name', value);
                      }
                    }
                  }}
                  placeholder={mode === 'chat' ? "Nome do contato" : "Título do lead"}
                  maxLength={50}
                />
                <div className="character-count">
                  {`${editingCard?.title?.length || 0}/50`}
                </div>
              </div>

              {mode === 'chat' && (
                <div className="form-group">
                  <label htmlFor="edit-phone">Telefone <span className="required">*</span></label>
                  <input
                    id="edit-phone"
                    type="text"
                    value={editingCard?.phone || ''}
                    onChange={(e) => {
                      updateEditingCard('phone', e.target.value);
                      updateEditingCard('number', e.target.value);
                    }}
                    placeholder="Telefone"
                  />
                </div>
              )}

              <div className="form-group">
                <label htmlFor="edit-email">E-mail</label>
                <input
                  id="edit-email"
                  type="email"
                  value={editingCard?.email || ''}
                  onChange={(e) => updateEditingCard('email', e.target.value)}
                  placeholder="E-mail"
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-content">{mode === 'chat' ? 'Observações' : 'Descrição'}</label>
                <textarea
                  id="edit-content"
                  value={editingCard?.content || ''}
                  onChange={(e) => updateEditingCard('content', e.target.value)}
                  placeholder={mode === 'chat' ? "Observações sobre o contato" : "Descrição do lead"}
                  rows={3}
                />
              </div>

              {mode === 'crm' && (
                <div className="form-group">
                  <label>Prioridade</label>
                  <div className="contact-select">
                    <div
                      className="select-input"
                      onClick={() => setPriorityDropdownOpen(!isPriorityDropdownOpen)}
                    >
                      <input
                        type="text"
                        value={editingCard?.priority ?
                          editingCard.priority === 'high' ? 'Alta' :
                            editingCard.priority === 'medium' ? 'Média' : 'Baixa'
                          : ''
                        }
                        readOnly
                        placeholder="Selecione a prioridade"
                      />
                      <FiChevronDown className={`dropdown-icon ${isPriorityDropdownOpen ? 'open' : ''}`} />
                    </div>
                    {isPriorityDropdownOpen && (
                      <div className="contact-dropdown">
                        <div className="contact-option" onClick={() => {
                          updateEditingCard('priority', 'high');
                          setPriorityDropdownOpen(false);
                        }}>
                          High
                        </div>
                        <div className="contact-option" onClick={() => {
                          updateEditingCard('priority', 'medium');
                          setPriorityDropdownOpen(false);
                        }}>
                          Medium
                        </div>
                        <div className="contact-option" onClick={() => {
                          updateEditingCard('priority', 'low');
                          setPriorityDropdownOpen(false);
                        }}>
                          Low
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Etiqueta</label>
                <div className="contact-select">
                  <div
                    className="select-input"
                    onClick={() => setTagDropdownOpen(!isTagDropdownOpen)}
                  >
                    <input
                      type="text"
                      value={editingCard?.tagId ?
                        tags.find(t => t.id === editingCard.tagId)?.name || ''
                        : ''
                      }
                      readOnly
                      placeholder="Selecione uma etiqueta"
                    />
                    <FiChevronDown className={`dropdown-icon ${isTagDropdownOpen ? 'open' : ''}`} />
                  </div>
                  {isTagDropdownOpen && (
                    <div className="contact-dropdown">
                      <div
                        className="contact-option"
                        onClick={() => {
                          updateEditingCard('tagId', undefined);
                          setTagDropdownOpen(false);
                        }}
                      >
                        No tag
                      </div>
                      {tags.map(tag => (
                        <div
                          key={tag.id}
                          className="contact-option"
                          onClick={() => {
                            updateEditingCard('tagId', tag.id);
                            setTagDropdownOpen(false);
                          }}
                        >
                          {tag.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Responsável</label>
                <div className="contact-select">
                  <div
                    className="select-input"
                    onClick={() => setResponsibleDropdownOpen(!isResponsibleDropdownOpen)}
                  >
                    <input
                      type="text"
                      value={editingCard?.assignedTo ?
                        users.find(u => Number(u.id) === Number(editingCard.assignedTo))?.name || ''
                        : ''
                      }
                      readOnly
                      placeholder="Selecione o responsável"
                    />
                    <FiChevronDown className={`dropdown-icon ${isResponsibleDropdownOpen ? 'open' : ''}`} />
                  </div>
                  {isResponsibleDropdownOpen && (
                    <div className="contact-dropdown">
                      <div
                        className="contact-option"
                        onClick={() => {
                          updateEditingCard('assignedTo', null);
                          setResponsibleDropdownOpen(false);
                        }}
                      >
                        No Assignee
                      </div>
                      {users.map(user => (
                        <div
                          key={user.id}
                          className="contact-option"
                          onClick={() => {
                            updateEditingCard('assignedTo', Number(user.id));
                            setResponsibleDropdownOpen(false);
                          }}
                        >
                          {user.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="card-sidebar-section">
                <h3><FiUser /> Dados do contato</h3>
                <div className="card-sidebar-info">
                  {editingCard?.email ? (
                    <div className="card-sidebar-info-item">
                      <span className="info-label"><FiMail /> E-mail:</span>
                      <span className="info-value">{editingCard.email}</span>
                    </div>
                  ) : (
                    <div className="card-sidebar-info-item empty">
                      <span className="info-label"><FiMail /> E-mail:</span>
                      <span className="info-value empty">Not provided</span>
                    </div>
                  )}
                  {mode === 'crm' && (
                    (editingCard?.phone || editingCard?.number) ? (
                      <div className="card-sidebar-info-item">
                        <span className="info-label"><FiPhone /> Telefone:</span>
                        <span className="info-value card-sidebar-phone-digits">
                          {isLidOrJid(editingCard.phone || editingCard.number)
                            ? PHONE_UNAVAILABLE
                            : formatPhoneNumber(editingCard.phone || editingCard.number || '')}
                        </span>
                      </div>
                    ) : (
                      <div className="card-sidebar-info-item empty">
                        <span className="info-label"><FiPhone /> Telefone:</span>
                        <span className="info-value empty">Not provided</span>
                      </div>
                    )
                  )}
                  {mode === 'chat' && (
                    (editingCard?.phone || editingCard?.number) ? (
                      <div className="card-sidebar-info-item">
                        <span className="info-label"><FiPhone /> Telefone:</span>
                        <span className="info-value">{getDisplayPhone(editingCard.phone || editingCard.number)}</span>
                      </div>
                    ) : (
                      <div className="card-sidebar-info-item empty">
                        <span className="info-label"><FiPhone /> Telefone:</span>
                        <span className="info-value empty">Not provided</span>
                      </div>
                    )
                  )}
                </div>
              </div>

              {(commercialFields.length > 0 || commercialHistory.length > 0) && (
                <div className="card-sidebar-section">
                  <h3><FiInfo /> Cadastro</h3>
                  <CommercialFieldList properties={commercialFields} history={commercialHistory} />
                </div>
              )}

              <div className="card-sidebar-section">
                <h3><FiInfo /> {mode === 'chat' ? 'Situação do contato' : 'Situação do lead'}</h3>
                <div className="card-sidebar-info">
                  {editingCard?.assignedTo ? (
                    <div className="card-sidebar-info-item">
                      <span className="info-label"><FiUser /> Responsável:</span>
                      <span className="info-value">
                        {users.find(u => Number(u.id) === Number(editingCard.assignedTo))?.name || 'Não encontrado'}
                      </span>
                    </div>
                  ) : (
                    <div className="card-sidebar-info-item empty">
                      <span className="info-label"><FiUser /> Responsável:</span>
                      <span className="info-value empty">Sem responsável</span>
                    </div>
                  )}

                  {editingCard?.createdAt && (
                    <div className="card-sidebar-info-item">
                      <span className="info-label"><FiCalendar /> Criado em:</span>
                      <span className="info-value">{formatDate(editingCard.createdAt)}</span>
                    </div>
                  )}
                  {mode === 'crm' && editingCard?.priority ? (
                    <div className="card-sidebar-info-item">
                      <span className="info-label"><FiFlag /> Prioridade:</span>
                      <span className={`info-value priority-label ${editingCard.priority}`}>
                        {editingCard.priority === 'high' ? 'Alta' :
                          editingCard.priority === 'medium' ? 'Média' : 'Baixa'}
                      </span>
                    </div>
                  ) : mode === 'crm' && (
                    <div className="card-sidebar-info-item empty">
                      <span className="info-label"><FiFlag /> Prioridade:</span>
                      <span className="info-value empty">Não definida</span>
                    </div>
                  )}
                </div>
              </div>

              {mode === 'crm' && !isEditing && !isNewCard && editingCard?.contactId && Number(editingCard.id) > 0 && (
                <div className="card-sidebar-section">
                  <button
                    type="button"
                    className="sidebar-button secondary"
                    onClick={() => navigate(`/crm/oportunidade/${editingCard.id}`)}
                  >
                    Abrir ficha completa
                  </button>
                  <OpportunityPanel
                    sectorId={editingCard.sectorId || SessionService.getSectorId() || 0}
                    contactId={editingCard.contactId}
                    cardId={Number(editingCard.id)}
                    columns={columns}
                    onChanged={onOpportunitiesChanged}
                    onOpenDossier={(id) => navigate(`/crm/oportunidade/${id}`)}
                    addToast={addToast}
                  />
                </div>
              )}

              {mode === 'crm' && !isEditing && !isNewCard && (
                <div className="card-sidebar-section card-sidebar-section--automation-logs">
                  <h3><FiZap /> Movimentações</h3>
                  <p className="card-sidebar-automation-hint">
                    Cada mudança de etapa desta oportunidade, feita pela IA ou por uma pessoa, com o motivo e a evidência.
                  </p>
                  {crmAutomationLogsLoading ? (
                    <div className="card-sidebar-automation-loading">Carregando…</div>
                  ) : cardLogs.length === 0 ? (
                    <div className="card-sidebar-automation-empty">Nenhuma movimentação registrada ainda.</div>
                  ) : (
                    <ul className="card-sidebar-automation-list">
                      {cardLogs.map(log => {
                        const route = moveRouteLabel(log);
                        const confidence = formatConfidence(log.confidence);
                        return (
                          <li key={log.id} className="card-sidebar-automation-item">
                            <div className="card-sidebar-automation-item__meta">
                              <span className="card-sidebar-automation-item__time">
                                {formatDate(log.createdAt)} · {moveAuthorLabel(log)}
                              </span>
                              <span className={`card-sidebar-automation-outcome card-sidebar-automation-outcome--${moveOutcomeTone(log.outcome)}`}>
                                {moveOutcomeLabel(log.outcome)}
                              </span>
                            </div>
                            <div className="card-sidebar-automation-item__body">
                              {route && <div className="card-sidebar-automation-route">{route}</div>}
                              {log.detail != null && log.detail !== '' && (
                                <div><strong>Motivo:</strong> {log.detail}</div>
                              )}
                              {log.evidence != null && log.evidence.trim() !== '' && (
                                <div className="card-sidebar-automation-evidence">“{log.evidence.trim()}”</div>
                              )}
                              {confidence && (
                                <div className="card-sidebar-automation-detail">Confiança: {confidence}</div>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="card-sidebar-footer">
          {isEditing || isNewCard ? (
            <>
              <button
                type="button"
                className="sidebar-button secondary"
                onClick={cancelEditing}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="sidebar-button primary"
                onClick={saveChanges}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <LoadingSpinner size="sm" color="white" className="card-sidebar-save-spinner" />
                    <span>Saving…</span>
                  </>
                ) : (
                  <>
                    <FiSave />{' '}
                    {isNewCard
                      ? mode === 'chat'
                        ? 'Criar contato'
                        : 'Criar lead'
                      : 'Salvar'}
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                className="sidebar-button primary"
                onClick={startEditing}
              >
                <FiEdit /> Editar
              </button>
              <button
                className="sidebar-button danger"
                onClick={() => {
                  if (window.confirm(mode === 'chat'
                    ? 'Remover este contato desta lista? Isso é diferente de arquivar.'
                    : 'Excluir este lead?')) {
                    onClose();
                  }
                }}
              >
                <FiTrash2 /> Excluir
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CardSidebar; 